import { AppType } from "client/Api/Apps/common";
import {
	ClientSpaceModel,
	ClientTileGroupModel,
	ClientTileGroupSpacePreferenceModel,
	ClientTileModel,
	ConditionalAccessDisabledApps,
	ConditionalAccessDisabledTileGroup,
	MaintenanceWindowModel,
	SaveTileGroupModel,
	SaveTileModel,
	SpaceData,
	TileGroupSpacePreferenceData
} from "client/Api/Workspace/workspace";
import { never } from "client_legacy/ndaw/errors";
import { convertDatabaseOrTempObjectIdToDto, DatabaseId } from "client_legacy/ndaw/tempOrDatabaseId";
import {
	AppTypeAndTileSettings,
	SpaceRenderState,
	TileConditionalAccess,
	TileData,
	TileGroupData,
	TileGroupInitializationModel,
	TileGroupRenderState,
	TileMaintenanceWindow,
	TileRenderState,
	WorkspaceInitializationModel
} from "./common";
import { parseDtoDate } from "client/DateAndTime/dateTimeUtils";
import { fromArray } from "client_legacy/ndaw/mapHelpers";
import { sortBy } from "client_legacy/ndaw/arrayHelpers";
import * as tileGroupSelector from "./selectors/tileGroup";

export function convertToInitialization(
	tileGroups: readonly ClientTileGroupModel[],
	maintenanceWindows: readonly MaintenanceWindowModel[],
	conditionalAccessDisabledApps: readonly ConditionalAccessDisabledApps[],
	conditionalAccessDisabledTileGroups: readonly ConditionalAccessDisabledTileGroup[]
): WorkspaceInitializationModel {
	const conditionalAccessPerTileGroup: Map<number, ConditionalAccessDisabledTileGroup> = fromArray(
		conditionalAccessDisabledTileGroups,
		(x) => x.tileGroupId
	);

	const conditionalAccessPerTile = new Map<number, TileConditionalAccess>();

	for (const model of conditionalAccessDisabledApps) {
		const connectionAccess: TileConditionalAccess = {
			reason: model.reason,
			notificationMessage: model.reason,
			hideTile: model.hideTiles
		};

		for (const tileId of model.tileIds) {
			if (conditionalAccessPerTile.has(tileId)) {
				continue;
			}

			conditionalAccessPerTile.set(tileId, connectionAccess);
		}
	}

	const maintenanceWindowPerTile = new Map<number, TileMaintenanceWindow>();

	for (const model of maintenanceWindows) {
		const maintenanceWindow: TileMaintenanceWindow = {
			endsOn: parseDtoDate(model.endsOn),
			notificationMessage: model.notificationMessage
		};

		for (const tileId of model.tileIds) {
			if (maintenanceWindowPerTile.has(tileId)) {
				continue;
			}

			maintenanceWindowPerTile.set(tileId, maintenanceWindow);
		}
	}

	const groups: TileGroupInitializationModel[] = [];

	for (const tileGroup of tileGroups) {
		const tileGroupData: TileGroupData = convertTileGroup(
			tileGroup,
			conditionalAccessPerTileGroup.get(tileGroup.id)
		);

		const tiles: readonly TileData[] = tileGroup.tiles
			.map(
				(x) => convertTile(
					x,
					conditionalAccessPerTile.get(x.id),
					maintenanceWindowPerTile.get(x.id)
				)
			);

		groups.push({
			data: tileGroupData,
			tiles
		});
	}

	return {
		groups
	};
}

export function convertTileGroup(
	model: ClientTileGroupModel,
	conditionalAccess: ConditionalAccessDisabledTileGroup | nil
): TileGroupData {
	const {
		id,
		type,
		width,
		name,
		accessKind,
		isHiddenByDefaultOnPersonalSpace
	} = model;

	return {
		id: new DatabaseId(id),
		kind: type,
		width,
		name,
		accessKind,
		isHiddenByDefaultOnPersonalSpace,
		conditionalAccess
	};
}

export function convertTile(
	model: ClientTileModel,
	conditionalAccess: TileConditionalAccess | nil,
	maintenanceWindow: TileMaintenanceWindow | nil
): TileData {
	const kindAndSettings: AppTypeAndTileSettings = convertAppSpecificSettings(model);

	const {
		id,
		environmentAppId,
		name,
		imageDisplayInfo,
		color,
		position,
		dimension,
		isConfiguredCorrectly,
		appIsNotAccessible,
		appIsLicensed,
		shouldHideName
	} = model;

	return {
		...kindAndSettings,
		id,
		environmentAppId,
		name,
		icon: imageDisplayInfo,
		color,
		position,
		dimension,
		isConfiguredCorrectly,
		appIsNotAccessible,
		appIsLicensed,
		conditionalAccess,
		maintenanceWindow,
		shouldHideName
	};
}

export function convertStateToWorkspaceInitializationModel(
	state: SpaceRenderState
): WorkspaceInitializationModel {
	return {
		groups: state.groups
			.map(convertStateToTileGroupInitializationModel)
	};
}

export function convertStateToTileGroupSpacePreferenceData(
	state: SpaceRenderState
): readonly TileGroupSpacePreferenceData[] {
	const orderedGroups: TileGroupRenderState[] = sortBy(
		state.groups,
		x => x.order
	);

	return orderedGroups.map(
		group => {
			const isHidden: boolean = tileGroupSelector.isHidden(
				group,
				state.options
			);

			return {
				tileGroupId: group.data.id,
				isHidden
			};
		}
	);
}

export function convertTileGroupStateToSaveModel(state: TileGroupRenderState): SaveTileGroupModel {
	const {
		data,
		tileGrid
	} = state;

	return {
		id: convertDatabaseOrTempObjectIdToDto(data.id),
		isHiddenByDefaultOnPersonalSpace: data.isHiddenByDefaultOnPersonalSpace,
		name: data.name,
		width: data.width,
		type: data.kind,
		tiles: tileGrid.tiles
			.map(convertTileStateToSaveModel)
	};
}

function appTypeAndTileSettings<TModel extends ClientTileModel , TKey extends keyof TModel>(
	model: TModel,
	keys: readonly TKey[]
): { type: TModel["type"]; settings: Pick<TModel, typeof keys[number]>; } {
	const settings: Partial<Pick<TModel, typeof keys[number]>> = {};
	for (const key of keys) {
		settings[key] = model[key];
	}

	return {
		type: model.type,
		settings: settings as Pick<TModel, typeof keys[number]>
	};
}

const DESTINATION_WINDOW = "destinationWindow";
const DESTINATION_APP = "destinationApp";
const TABS = "tabs";
const CONNECTION_ID = "connectionId";
const CONNECTION = "connection";
const HIDE_HEADER = "hideHeader";
const HIDE_FOOTER = "hideFooter";
const ENABLED_ACTIONS = "enabledActions";

function convertAppSpecificSettings(model: ClientTileModel): AppTypeAndTileSettings {
	switch (model.type) {
		case AppType.Birthday:
		case AppType.CitrixXenApp:
		case AppType.HowTo:
		case AppType.LocalApp:
		case AppType.Office365Excel:
		case AppType.Office365LyncMeeting:
		case AppType.Office365OneNote:
		case AppType.Office365PeopleScreen:
		case AppType.Office365PowerPoint:
		case AppType.Office365Word:
		case AppType.Unknown:
			return appTypeAndTileSettings(model, []);
		case AppType.Url:
			return appTypeAndTileSettings(model, [DESTINATION_WINDOW]);
		case AppType.Email:
			return appTypeAndTileSettings(
				model,
				[
					DESTINATION_APP,
					DESTINATION_WINDOW,
					TABS
				]
			);
		case AppType.Documents:
			return appTypeAndTileSettings(
				model,
				[
					DESTINATION_WINDOW,
					TABS
				]
			);
		case AppType.Office365Calendar:
			return appTypeAndTileSettings(
				model,
				[
					DESTINATION_APP,
					DESTINATION_WINDOW
				]
			);
		case AppType.Rss:
			return appTypeAndTileSettings(model, [TABS]);
		case AppType.AzureApp:
			return appTypeAndTileSettings(model, [DESTINATION_WINDOW]);
		case AppType.ClientlessRdp:
			return appTypeAndTileSettings(model, [DESTINATION_WINDOW]);
		case AppType.PowerBi:
			return appTypeAndTileSettings(
				model,
				[
					"powerBiTileId",
					"dashboardId",
					"tokenInfo",
					"embedUrlBase"
				]
			);
		case AppType.Yammer:
			return appTypeAndTileSettings(model, [TABS]);
		case AppType.Embed:
			return appTypeAndTileSettings(
				model,
				[
					"navigationUrl",
					HIDE_HEADER,
					HIDE_FOOTER
				]
			);
		case AppType.ToDo:
			return appTypeAndTileSettings(model, [DESTINATION_WINDOW]);
		case AppType.Google:
			return appTypeAndTileSettings(model, [DESTINATION_WINDOW]);
		case AppType.TopDesk:
			return appTypeAndTileSettings(
				model,
				[
					DESTINATION_WINDOW,
					CONNECTION
				]
			);
		case AppType.ConnectWise:
			return appTypeAndTileSettings(model, [DESTINATION_WINDOW]);
		case AppType.Teams:
			return appTypeAndTileSettings(
				model,
				[
					ENABLED_ACTIONS,
					DESTINATION_WINDOW
				]
			);
		case AppType.SharePoint:
			return appTypeAndTileSettings(
				model,
				[
					ENABLED_ACTIONS,
					DESTINATION_WINDOW
				]
			);
		case AppType.AddressBook:
			return appTypeAndTileSettings(
				model,
				[
					"contactSources",
					"defaultView"
				]
			);
		case AppType.Hub:
			return appTypeAndTileSettings(
				model,
				[
					"layout",
					HIDE_HEADER,
					HIDE_FOOTER
				]
			);
		case AppType.VilansKick:
			return appTypeAndTileSettings(
				model,
				[
					DESTINATION_WINDOW,
					CONNECTION
				]
			);
		case AppType.Autotask:
			return appTypeAndTileSettings(
				model,
				[
					DESTINATION_WINDOW,
					CONNECTION
				]
			);
		case AppType.YouTube:
			return appTypeAndTileSettings(
				model,
				[
					"videoId",
					"playlistId",
					HIDE_HEADER,
					HIDE_FOOTER
				]
			);
		case AppType.NedapOns:
			return appTypeAndTileSettings(
				model,
				[
					DESTINATION_WINDOW,
					CONNECTION,
					TABS
				]
			);
		case AppType.Afas:
			return appTypeAndTileSettings(
				model,
				[
					DESTINATION_WINDOW,
					CONNECTION_ID,
					TABS
				]
			);
		case AppType.Zaaksysteem:
			return appTypeAndTileSettings(
				model,
				[
					DESTINATION_WINDOW,
					CONNECTION_ID,
					TABS
				]
			);
		case AppType.Zermelo:
			return appTypeAndTileSettings(
				model,
				[
					DESTINATION_WINDOW,
					CONNECTION_ID
				]
			);
		case AppType.ZenyaDoc:
			return appTypeAndTileSettings(
				model,
				[
					DESTINATION_WINDOW,
					CONNECTION_ID,
					TABS
				]
			);
		case AppType.Liquit:
			return appTypeAndTileSettings(
				model,
				[
					DESTINATION_WINDOW,
					CONNECTION_ID
				]
			);
		case AppType.VmwareHorizonApp:
			return appTypeAndTileSettings(model, [DESTINATION_WINDOW]);
		case AppType.Magister:
			return appTypeAndTileSettings(
				model,
				[
					DESTINATION_WINDOW,
					CONNECTION_ID,
					TABS
				]
			);
		case AppType.Integration:
			return appTypeAndTileSettings(
				model,
				[
					"navigationUrl",
					CONNECTION_ID,
					DESTINATION_WINDOW,
					HIDE_HEADER,
					HIDE_FOOTER
				]
			);
		case AppType.WindowsVirtualDesktop:
			return appTypeAndTileSettings(
				model,
				[
					"clientKind"
				]
			);
		default:
			never(model);
	}
}

export function convertToSpaceData(spaceModel: ClientSpaceModel): SpaceData {
	return {
		id: spaceModel.id,
		name: spaceModel.name,
		kind: spaceModel.kind,
		tileGroupsPreferences: spaceModel.tileGroupsPreferences
			.map(p => convertToTileGroupSpacePreferenceData(p)),
		accessKind: spaceModel.accessKind
	};
}

function convertToTileGroupSpacePreferenceData(
	spacePreferenceModel: ClientTileGroupSpacePreferenceModel
): TileGroupSpacePreferenceData {
	return {
		tileGroupId: new DatabaseId(spacePreferenceModel.tileGroupId),
		isHidden: spacePreferenceModel.isHidden
	};
}

function convertTileStateToSaveModel(state: TileRenderState): SaveTileModel {
	const {
		data
	} = state;

	return {
		id: data.id,
		dimension: data.dimension,
		position: data.position
	};
}

function convertStateToTileGroupInitializationModel(tileGroupRenderState: TileGroupRenderState): TileGroupInitializationModel {
	return {
		data: tileGroupRenderState.data,
		tiles: tileGroupRenderState.tileGrid
			.tiles
			.map(t => t.data)
	};
}